/**
 * Desktop composable
 * Manages state and logic for the admin desktop feature
 */

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../../app/store/auth'
import {
  subscribeToDesktopShortcuts,
  saveDesktopShortcut,
  updateShortcutPosition,
  deleteDesktopShortcut,
} from '../services/desktop'
import {
  getAvailableShortcutsForRole,
  calculateGridDimensions,
  findNearestEmptyCell,
  getCellPosition,
  GRID_CONFIG,
} from '../config/desktopShortcuts'
import { useToast } from './useToast'

export function useDesktop() {
  const authStore = useAuthStore()
  const { success, error: showError, info } = useToast()

  // ============================================
  // State
  // ============================================

  // Stored shortcuts from Firestore
  const shortcuts = ref({})

  // Loading state
  const isLoading = ref(true)

  // Modal state
  const isModalOpen = ref(false)

  // Currently dragging shortcut (for visual feedback)
  const draggingShortcut = ref(null)

  // Unsubscribe function
  let unsubscribe = null

  // ============================================
  // Computed
  // ============================================

  const userId = computed(() => authStore.userId)
  const userRole = computed(() => authStore.userRole)

  // Shortcuts as array for iteration
  const shortcutsList = computed(() => Object.values(shortcuts.value))

  // Available shortcuts filtered by user role
  const availableShortcuts = computed(() => getAvailableShortcutsForRole(userRole.value || 'user'))

  // Set of already added shortcut IDs (for filtering in modal)
  const addedShortcutIds = computed(() => new Set(shortcutsList.value.map((s) => s.id)))

  // ============================================
  // Methods
  // ============================================

  /**
   * Start listening to shortcuts from Firestore
   */
  function startListening() {
    if (!userId.value) {
      isLoading.value = false
      return
    }

    unsubscribe = subscribeToDesktopShortcuts(userId.value, (data, err) => {
      if (err) {
        showError('שגיאה בטעינת שולחן העבודה')
        isLoading.value = false
        return
      }
      shortcuts.value = data
      isLoading.value = false
    })
  }

  /**
   * Add a new shortcut to the desktop
   * New shortcuts are placed in the phone zone (cols 0-5) so they're visible on all devices
   * @param {Object} shortcutConfig - Shortcut configuration from desktopShortcuts.js
   * @param {number} canvasWidth - Canvas width for grid calculation (optional)
   * @param {number} canvasHeight - Canvas height for grid calculation (optional)
   */
  async function addShortcut(
    shortcutConfig,
    canvasWidth = window.innerWidth,
    canvasHeight = window.innerHeight - 120
  ) {
    if (!userId.value) return

    // Calculate occupied cells
    const occupiedCells = new Set()
    shortcutsList.value.forEach((s) => {
      if (s.gridCol !== undefined && s.gridRow !== undefined) {
        occupiedCells.add(`${s.gridCol},${s.gridRow}`)
      }
    })

    // Calculate grid dimensions dynamically
    const { rows: maxRows } = calculateGridDimensions(canvasWidth, canvasHeight)

    // Place new shortcuts only in phone zone (cols 0-5) so they're visible on all devices
    const phoneMaxCols = GRID_CONFIG.PHONE_COLS

    // Find first available cell within phone zone (start from top-right for RTL)
    const targetCell = findNearestEmptyCell(0, 0, occupiedCells, phoneMaxCols, maxRows)

    // Get position from cell
    const cellPos = getCellPosition(targetCell.col, targetCell.row, canvasWidth, canvasHeight)

    const newShortcut = {
      id: shortcutConfig.id,
      type: 'route',
      routeName: shortcutConfig.routeName,
      label: shortcutConfig.label,
      icon: shortcutConfig.icon,
      position: {
        x: cellPos.x,
        y: cellPos.y,
      },
      gridCol: targetCell.col,
      gridRow: targetCell.row,
      createdAt: new Date(),
    }

    try {
      await saveDesktopShortcut(userId.value, newShortcut)
      success(`${shortcutConfig.label} נוסף לשולחן העבודה`)
    } catch (err) {
      console.error('Error adding shortcut:', err)
      showError('שגיאה בהוספת קיצור דרך')
    }
  }

  /**
   * Update shortcut position after drag
   * @param {string} shortcutId - Shortcut ID
   * @param {Object} position - { x, y, gridCol?, gridRow? } coordinates and grid info
   */
  async function updatePosition(shortcutId, position) {
    if (!userId.value) return

    // Optimistic update for smooth UX
    if (shortcuts.value[shortcutId]) {
      shortcuts.value[shortcutId].position = { x: position.x, y: position.y }
      if (position.gridCol !== undefined) {
        shortcuts.value[shortcutId].gridCol = position.gridCol
      }
      if (position.gridRow !== undefined) {
        shortcuts.value[shortcutId].gridRow = position.gridRow
      }
    }

    try {
      await updateShortcutPosition(userId.value, shortcutId, position)
    } catch (err) {
      console.error('Error updating position:', err)
      // Revert will happen on next Firestore sync
    }
  }

  /**
   * Remove a shortcut from desktop
   * @param {string} shortcutId - Shortcut ID
   */
  async function removeShortcut(shortcutId) {
    if (!userId.value) return

    try {
      await deleteDesktopShortcut(userId.value, shortcutId)
      info('קיצור הדרך הוסר')
    } catch (err) {
      console.error('Error removing shortcut:', err)
      showError('שגיאה במחיקת קיצור דרך')
    }
  }

  /**
   * Open shortcuts modal
   */
  function openModal() {
    isModalOpen.value = true
  }

  /**
   * Close shortcuts modal
   */
  function closeModal() {
    isModalOpen.value = false
  }

  /**
   * Set dragging state
   * @param {string|null} shortcutId
   */
  function setDragging(shortcutId) {
    draggingShortcut.value = shortcutId
  }

  // ============================================
  // Lifecycle
  // ============================================

  onMounted(() => {
    startListening()
  })

  onUnmounted(() => {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  })

  return {
    // State
    shortcuts,
    shortcutsList,
    isLoading,
    isModalOpen,
    draggingShortcut,

    // Computed
    availableShortcuts,
    addedShortcutIds,

    // Methods
    addShortcut,
    updatePosition,
    removeShortcut,
    openModal,
    closeModal,
    setDragging,
  }
}
